import React from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertTriangle } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'Terms & Conditions', path: '/terms' }]} />

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Terms and Conditions</h1>
          <p className="text-gray-400">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Important Notice */}
        <Alert className="mb-8 border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <AlertDescription className="text-gray-300 ml-2">
            <strong className="text-white">Please Read Carefully:</strong> By accessing and using Options Analogies, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our Site.
          </AlertDescription>
        </Alert>

        <div className="space-y-8">
          {/* Acceptance */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms and Conditions ("Terms") govern your access to and use of Options Analogies (the "Site") operated by Datatomic Labs ("we," "us," or "our"). By accessing or using the Site, you accept and agree to be bound by these Terms and our Privacy Policy.
              </p>
            </CardContent>
          </Card>

          {/* Educational Purpose */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">2. Educational Purpose Only</h2>
              <div className="space-y-3 text-gray-300">
                <p>
                  All content on this Site is provided for <strong className="text-white">educational purposes only</strong>. The information presented is not financial advice, investment advice, trading advice, or any other type of advice.
                </p>
                <p className="font-semibold text-amber-300">
                  You should not rely on this information as a substitute for professional financial advice. Always consult with a qualified financial advisor before making investment decisions.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* User Responsibilities */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">3. User Responsibilities</h2>
              <div className="space-y-3 text-gray-300">
                <p>By using this Site, you agree to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the Site only for lawful purposes and in accordance with these Terms</li>
                  <li>Not engage in any conduct that restricts or inhibits anyone's use of the Site</li>
                  <li>Not attempt to gain unauthorized access to any portion of the Site</li>
                  <li>Not use any automated system or software to extract data from the Site</li>
                  <li>Not reproduce, distribute, or create derivative works without permission</li>
                  <li>Understand that options trading involves substantial risk of loss</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Intellectual Property */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">4. Intellectual Property Rights</h2>
              <div className="space-y-3 text-gray-300">
                <p>
                  The Site and its entire contents, features, and functionality (including but not limited to all information, text, displays, graphics, photographs, analogies, and the selection and arrangement thereof) are owned by Datatomic Labs and are protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p>
                  You may download and print content for personal, non-commercial use only. Any other use requires our prior written consent.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Disclaimers */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">5. Disclaimers and Limitations</h2>
              <div className="space-y-3 text-gray-300">
                <h3 className="text-lg font-semibold text-emerald-400">No Warranties</h3>
                <p>
                  THE SITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                
                <h3 className="text-lg font-semibold text-emerald-400 mt-4">Accuracy of Information</h3>
                <p>
                  While we strive for accuracy, we make no representations or warranties about the completeness, reliability, or accuracy of the information on the Site. Any reliance you place on such information is at your own risk.
                </p>

                <h3 className="text-lg font-semibold text-emerald-400 mt-4">Market Risk</h3>
                <p>
                  Options trading involves substantial risk of loss. Past performance does not guarantee future results. You may lose all capital invested.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">6. Limitation of Liability</h2>
              <div className="space-y-3 text-gray-300">
                <p>
                  TO THE FULLEST EXTENT PERMITTED BY LAW, DATATOMIC LABS AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your use or inability to use the Site</li>
                  <li>Any trading or investment decisions made based on Site content</li>
                  <li>Any unauthorized access to or use of our servers</li>
                  <li>Any interruption or cessation of transmission to or from the Site</li>
                  <li>Any bugs, viruses, or other harmful code</li>
                  <li>Any errors or omissions in any content</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Third-Party Links */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">7. Third-Party Links and Advertising</h2>
              <div className="space-y-3 text-gray-300">
                <p>
                  The Site may contain links to third-party websites and advertisements. We are not responsible for the content, accuracy, or practices of these third-party sites. We do not endorse products or services advertised on our Site.
                </p>
                <p>
                  Third-party advertisers may use cookies and other tracking technologies. We have no control over these technologies or the resulting information.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Indemnification */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">8. Indemnification</h2>
              <p className="text-gray-300 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Datatomic Labs, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from your use of the Site, violation of these Terms, or violation of any rights of another.
              </p>
            </CardContent>
          </Card>

          {/* Changes to Terms */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">9. Changes to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Site after changes constitutes acceptance of the modified Terms. We encourage you to review these Terms periodically.
              </p>
            </CardContent>
          </Card>

          {/* Termination */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">10. Termination</h2>
              <p className="text-gray-300 leading-relaxed">
                We may terminate or suspend your access to the Site immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Site will immediately cease.
              </p>
            </CardContent>
          </Card>

          {/* Governing Law */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">11. Governing Law</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Datatomic Labs operates, without regard to its conflict of law provisions.
              </p>
            </CardContent>
          </Card>

          {/* Severability */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">12. Severability</h2>
              <p className="text-gray-300 leading-relaxed">
                If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall remain in full force and effect.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">13. Contact Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have questions about these Terms and Conditions, please contact us:
              </p>
              <div className="text-gray-300">
                <p><strong className="text-white">Email:</strong> <a href="mailto:contact@datatomiclabs.com" className="text-emerald-400 hover:text-emerald-300 underline">contact@datatomiclabs.com</a></p>
                <p className="mt-2"><strong className="text-white">Company:</strong> Datatomic Labs</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Terms;